<template>
  <div class="bg-white text-gray-800">
    <section class="relative">
      <img
        :src="partner.headerImage"
        :alt="`${partner.name} header`"
        class="h-[280px] md:h-[420px] w-full object-cover"
      />
      <div class="absolute inset-0 bg-black/35" />
      <div class="absolute bottom-0 left-0 right-0">
        <div class="max-w-6xl mx-auto px-4 pb-6 md:pb-10 text-white">
          <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p class="text-sm md:text-base opacity-90">{{ partner.category }}</p>
              <h1 class="text-3xl md:text-5xl font-headings">{{ partner.name }}</h1>
              <p class="mt-2 text-sm md:text-base">{{ partner.location }}</p>
            </div>
            <img
              :src="partner.logoImage"
              :alt="`${partner.name} logo`"
              class="h-16 md:h-20 w-auto object-contain bg-white/95 p-2 rounded-lg shadow"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="max-w-6xl mx-auto px-4 py-10 md:py-14">
      <div class="grid md:grid-cols-3 gap-8">
        <div class="md:col-span-2">
          <h2 class="text-2xl md:text-3xl font-headings mb-4">About {{ partner.name }}</h2>
          <p class="text-gray-700 leading-relaxed">{{ partner.shortDescription }}</p>
        </div>

        <aside class="bg-primary-50 rounded-xl p-5">
          <div class="flex flex-wrap gap-2 mb-4">
            <span class="px-3 py-1 rounded-full bg-white text-primary-700 border border-primary-200 text-xs font-semibold">
              5+ Years
            </span>
            <span class="px-3 py-1 rounded-full bg-white text-primary-700 border border-primary-200 text-xs font-semibold">
              Accredited Trainer
            </span>
          </div>
          <h3 class="font-semibold text-lg mb-3">Partner Summary</h3>
          <div class="space-y-3 text-sm">
            <div>
              <p class="font-semibold text-gray-900">Regions Served</p>
              <p>{{ partner.regionsServed.join(', ') }}</p>
            </div>
            <div>
              <p class="font-semibold text-gray-900">Services Offered</p>
              <ul class="list-disc ml-5">
                <li v-for="service in partner.servicesOffered" :key="service">{{ service }}</li>
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </section>

    <section class="max-w-6xl mx-auto px-4 pb-10">
      <h2 class="text-2xl md:text-3xl font-headings mb-5">Team</h2>
      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <article v-for="member in partner.team" :key="member.name" class="border rounded-xl p-4">
          <img
            @click="viewImage(member.headshot)"
            :src="member.headshot"
            :alt="member.name"
            class="h-48 w-full object-cover rounded-lg mb-3"
          />
          <h3 class="font-semibold text-lg">{{ member.name }}</h3>
          <p class="text-sm text-gray-600">{{ member.title }}</p>
        </article>
      </div>
    </section>

    <section class="max-w-6xl mx-auto px-4 pb-10">
      <h2 class="text-2xl md:text-3xl font-headings mb-5">Gallery</h2>
      <div
        class="relative overflow-hidden rounded-xl"
        @mouseenter="pauseAutoGallery"
        @mouseleave="resumeAutoGallery"
      >
        <div
          class="flex transition-transform duration-500 ease-in-out"
          :style="{ transform: `translateX(-${galleryIndex * 100}%)` }"
        >
          <div
            v-for="(image, index) in partner.gallery"
            :key="`${image}-${index}`"
            class="w-full flex-shrink-0"
          >
            <img
            @click="viewImage(image)"
              :src="image"
              :alt="`${partner.name} gallery ${index + 1}`"
              class="h-56 md:h-[420px] w-full object-cover rounded-xl cursor-pointer"
            />
          </div>
        </div>

        <button
          @click="prevGallery(true)"
          class="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow cursor-pointer"
          aria-label="Previous gallery image"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <button
          @click="nextGallery(true)"
          class="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow cursor-pointer"
          aria-label="Next gallery image"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div class="flex justify-center gap-2 mt-4">
        <img
          v-for="(image, index) in partner.gallery"
          :key="`${image}-${index}`"
          :src="image"
          :alt="`${partner.name} gallery thumbnail ${index + 1}`"
          class="h-12 w-16 object-cover rounded cursor-pointer border-2 transition-all"
          :class="galleryIndex === index ? 'border-primary-500 opacity-100' : 'border-transparent opacity-70 hover:opacity-100'"
          @click="goToGallery(index, true)"
        />
      </div>
    </section>

    <section class="max-w-6xl mx-auto px-4 pb-10 grid md:grid-cols-2 gap-8">
      <div>
        <h2 class="text-2xl md:text-3xl font-headings mb-4">Case Studies</h2>
        <ul class="space-y-3">
          <li
            v-for="study in partner.caseStudies"
            :key="study.title"
            class="border rounded-xl p-4"
          >
            <h3 class="font-semibold">{{ study.title }}</h3>
            <p class="text-sm text-gray-700 mt-1">{{ study.summary }}</p>
          </li>
        </ul>
      </div>

      <div>
        <h2 class="text-2xl md:text-3xl font-headings mb-4">Documents & Links</h2>
        <ul class="space-y-3">
          <li
            v-for="item in partner.documentsAndLinks"
            :key="item.title"
            class="border rounded-xl p-4 flex items-center justify-between gap-4"
          >
            <div>
              <h3 class="font-semibold">{{ item.title }}</h3>
              <p class="text-sm text-gray-600">{{ item.type }}</p>
            </div>
            <a :href="item.url" class="text-primary-700 hover:underline" target="_blank" rel="noreferrer">
              Open
            </a>
          </li>
        </ul>
      </div>
    </section>

    <section class="py-12 stacked-linear bg-no-repeat relative">
      <div
        class="h-full w-full absolute top-0 left-0 z-20 bg-no-repeat"
        style="background-position: right 16px bottom 16px; background-size: 200px; background-image: url('https://labs.entegy.com.au/entegy/rocket.svg')"
      />
      <Testimonials />
    </section>

    <section class="max-w-6xl mx-auto px-4 py-10 md:py-14">
      <div class="bg-secondary-50 border border-secondary-200 rounded-2xl p-6 md:p-8">
        <h2 class="text-2xl md:text-3xl font-headings mb-4">Enquiry Details</h2>
        <div class="grid md:grid-cols-3 gap-4 text-sm md:text-base">
          <div>
            <p class="font-semibold">Email</p>
            <a class="text-primary-700 hover:underline" :href="`mailto:${partner.enquiryDetails.email}`">
              {{ partner.enquiryDetails.email }}
            </a>
          </div>
          <div>
            <p class="font-semibold">Phone</p>
            <a class="text-primary-700 hover:underline" :href="`tel:${partner.enquiryDetails.phone}`">
              {{ partner.enquiryDetails.phone }}
            </a>
          </div>
          <div>
            <p class="font-semibold">Form</p>
            <a
              class="text-primary-700 hover:underline"
              :href="partner.enquiryDetails.formUrl"
              target="_blank"
              rel="noreferrer"
            >
              Submit an enquiry
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import Testimonials from '../components/Testimonials.vue'
import { onMounted, onUnmounted, ref, inject } from 'vue'
const viewImage = inject('viewImage')

const galleryIndex = ref(0)
let autoGalleryInterval = null
const hasUserInteracted = ref(false)

const partner = {
  name: 'Eventium',
  location: 'Melbourne, Australia',
  headerImage: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1600&q=80',
  logoImage: '/images/testimonials/eventium.png',
  category: 'Event Technology Partner',
  shortDescription:
    'Eventium delivers end-to-end event strategy and execution, combining digital engagement, production, and analytics to create high-impact conferences, exhibitions, and incentive programs.',
  regionsServed: ['Australia', 'New Zealand', 'Singapore'],
  servicesOffered: [
    'Event strategy and planning',
    'Registration and ticketing implementation',
    'On-site operations and staffing',
    'App, website and attendee engagement setup',
    'Post-event reporting and insights'
  ],
  team: [
    {
      name: 'Simone Badcock',
      title: 'Business Operations Manager',
      headshot: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=800&q=80'
    },
    {
      name: 'Mitchell Clarke',
      title: 'Head of Client Success',
      headshot: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=800&q=80'
    },
    {
      name: 'Ava Henderson',
      title: 'Senior Event Technologist',
      headshot: 'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?auto=format&fit=crop&w=800&q=80'
    }
  ],
  gallery: [
    'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1551818255-e6e10975bc17?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1503428593586-e225b39bddfe?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1464375117522-1311d6a5b81f?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1472653816316-3ad6f10a6592?auto=format&fit=crop&w=1200&q=80'
  ],
  caseStudies: [
    {
      title: 'National Leadership Summit',
      summary: 'Scaled registration and session engagement for 4,000 delegates across 3 days with live analytics.'
    },
    {
      title: 'Global Franchise Conference',
      summary: 'Delivered multilingual websites, badge printing, and lead capture across 5 regional activations.'
    }
  ],
  documentsAndLinks: [
    { title: 'Company Profile', type: 'Brochure (PDF)', url: '#' },
    { title: 'Services Deck', type: 'Presentation', url: '#' },
    { title: 'Website', type: 'External Link', url: 'https://www.entegy.com.au' }
  ],
  enquiryDetails: {
    email: 'partners@eventium.example',
    phone: '+61 3 9000 1234',
    formUrl: '/contact'
  }
}

const nextGallery = (userAction = false) => {
  if (userAction) {
    hasUserInteracted.value = true
    stopAutoGallery()
  }
  galleryIndex.value = (galleryIndex.value + 1) % partner.gallery.length
}

const prevGallery = (userAction = false) => {
  if (userAction) {
    hasUserInteracted.value = true
    stopAutoGallery()
  }
  galleryIndex.value = galleryIndex.value === 0
    ? partner.gallery.length - 1
    : galleryIndex.value - 1
}

const goToGallery = (index, userAction = false) => {
  if (userAction) {
    hasUserInteracted.value = true
    stopAutoGallery()
  }
  galleryIndex.value = index
}

const startAutoGallery = () => {
  if (autoGalleryInterval || hasUserInteracted.value) {
    return
  }

  autoGalleryInterval = setInterval(() => {
    nextGallery()
  }, 4000)
}

const stopAutoGallery = () => {
  if (autoGalleryInterval) {
    clearInterval(autoGalleryInterval)
    autoGalleryInterval = null
  }
}

const pauseAutoGallery = () => {
  stopAutoGallery()
}

const resumeAutoGallery = () => {
  startAutoGallery()
}

onMounted(() => {
  startAutoGallery()
})

onUnmounted(() => {
  stopAutoGallery()
})
</script>

<style scoped>
.stacked-linear {
  background:
    linear-gradient(217deg, #4f39f6, transparent 70.71%),
    linear-gradient(127deg, #7d38ff, transparent 70.71%),
    linear-gradient(336deg, #ff69b2, transparent 70.71%);
  background-size: 200% 200%;
  animation: gradientShift 10s ease infinite;
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}
</style>